import React from 'react';
import ColumnHeaderCommon from '@components/SchedulePage/ColumnHeaderCommon/ColumnHeaderCommon';
import { ScheduleColumnData } from '@data/schedules/types/ScheduleColumnData';
import { Box, Typography, Popper, ClickAwayListener, Link } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { getBlockColumnHeaderDetails } from '@components/SchedulePage/utilities/getBlockColumnHeaderDetails/getBlockColumnHeaderDetails';
import { useAnchorContext } from '@components/AnchorProvider/AnchorProvider';

interface AdditionalBlocksProps {
	blockInfo: ScheduleColumnData;
}

const useStyles = makeStyles((theme) =>
	createStyles({
		root: {
			backgroundColor: theme.palette.primary[50],
			color: theme.palette.common.black,
			display: 'flex',
			flexDirection: 'column',
			border: `2px solid ${theme.palette.primary.dark}`,
			padding: theme.spacing(0.5, 1, 0.5, 1),
			borderRadius: '4px',
		},
		additionalBlockContainer: {
			'marginBottom': theme.spacing(2),
			'&:last-child': {
				marginBottom: 0,
			},
		},
		headerText: {
			fontWeight: 600,
			cursor: 'pointer',
		},
		blockText: {
			backgroundColor: theme.palette.primary.dark,
			color: theme.palette.common.white,
			cursor: 'pointer',
			textDecoration: 'underline',
			...theme.typography.body2,
			fontWeight: 525,
			paddingTop: theme.spacing(0.5),
			paddingBottom: theme.spacing(0.5),
		},
		popoverWithArrow: {
			'scrollSnapAlign': 'start',
			'position': 'relative',
			'overflow': 'visible',
			'marginTop': theme.spacing(1.5),
			'&::before': {
				content: '""',
				position: 'absolute',
				top: -2,
				left: '50%',
				transform: 'translateX(-50%) rotate(45deg)',
				width: 14,
				height: 14,
				backgroundColor: theme.palette.primary.dark,
				zIndex: -1,
			},
			'zIndex': 1000,
			'minWidth': 300,
			'maxWidth': 300,
		},
	}),
);

const AdditionalBlocks: React.FC<AdditionalBlocksProps> = ({ blockInfo }) => {
	const classes = useStyles();
	const { handleSetAnchorEl, anchorEl, handleClearAnchorEl } = useAnchorContext();
	const prevRectRef = React.useRef<DOMRect | null>(null);

	const { additionalBlockTimeframes } = React.useMemo(
		() => getBlockColumnHeaderDetails(blockInfo), [blockInfo],
	);

	const openAdditionalBlocks = Boolean(anchorEl);

	React.useEffect(() => {
		if (!anchorEl) return;

		const checkPosition = () => {
			const rect = anchorEl.getBoundingClientRect();
			if (prevRectRef.current && Math.abs(rect.left - prevRectRef.current.left) > 20) {
				handleClearAnchorEl();
			}
			prevRectRef.current = rect;
		};

		window.addEventListener('scroll', checkPosition, true);

		return () => {
			window.removeEventListener('scroll', checkPosition, true);
		};
	}, [anchorEl, handleClearAnchorEl]);

	if (!Object.keys(additionalBlockTimeframes).length) {
		return null;
	}

	return (
		<>
			<Link
				href="#"
				onClick={(e) => {
					e.preventDefault();
					handleSetAnchorEl(e);
				}}
				onKeyDown={(e) => {
					if (e.key === 'Escape') {
						handleClearAnchorEl();
					}
				}}
				data-field="surgeonColumn-blockInfo-additionalBlocksLink"
				className={classes.blockText}
				aria-label={`Additional blocks for ${blockInfo.surgeon.name}`}
				underline="none"
			>
				Additional Blocks
			</Link>
			<Popper
				open={openAdditionalBlocks}
				anchorEl={anchorEl}
				placement="bottom"
				className={classes.popoverWithArrow}
			>
				<ClickAwayListener onClickAway={handleClearAnchorEl}>
					<Box className={classes.root}>
						{Object.keys(additionalBlockTimeframes)
							.filter((headerKey) => additionalBlockTimeframes[headerKey].length > 0)
							.map((headerKey) => (
								<Box key={headerKey} className={classes.additionalBlockContainer}>
									<Typography
										variant="body2"
										component="h3"
										className={classes.headerText}
									>
										{headerKey}
									</Typography>
									<ColumnHeaderCommon
										timeFrames={additionalBlockTimeframes[headerKey]}
									/>
								</Box>
							))}
					</Box>
				</ClickAwayListener>
			</Popper>
		</>
	);
};

export default AdditionalBlocks;
